import React from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faHandHoldingHeart,
  faHandshake,
  faHeart,
  faShieldAlt,
  faTrophy,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import Banner from './Banner';
import animaLogo from '../assets/anima-logo.svg';
import cpasLogo from '../assets/cpas-anderlecht-logo.png';
import cocofLogo from '../assets/cocof-francophones-bruxelles.png';
import mainTendueLogo from '../assets/LogoMark.png';
import partenamutLogo from '../assets/partenamut-logo.svg';
import '../styles/About.css';
import { useTranslation } from 'react-i18next';

const AnimatedStatNumber = ({ value, suffix = '', reducedMotion, label }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.65 });
  const [displayValue, setDisplayValue] = React.useState(reducedMotion ? value : 0);

  React.useEffect(() => {
    if (!isInView) return undefined;
    if (reducedMotion) {
      setDisplayValue(value);
      return undefined;
    }

    const duration = value <= 5 ? 650 : 950;
    let frameId;
    let startTime;

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(value * easedProgress));
      if (progress < 1) frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [isInView, reducedMotion, value]);

  return <h3 ref={ref} aria-label={`${value}${suffix} ${label}`}>{displayValue}{suffix}</h3>;
};

const About = () => {
  const { t, i18n } = useTranslation();
  const reducedMotion = useReducedMotion();
  const isDutch = i18n.resolvedLanguage?.startsWith('nl');
  const reveal = reducedMotion ? {} : {
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.18 }
  };

  const timelineItems = [
    {
      year: '2023',
      title: t('translate.about_title_time'),
      description: t('translate.about_dsc_time'),
      logo: mainTendueLogo,
      logoAlt: 'Main Tendue',
      logoClassName: 'journey-card-logo--main-tendue'
    },
    {
      year: '2023',
      title: t('translate.about_title_time2'),
      description: t('translate.about_dsc_time2'),
      logo: animaLogo,
      logoAlt: 'Anima',
      logoClassName: 'journey-card-logo--anima'
    },
    {
      year: '2024',
      title: t('translate.about_title_time3'),
      description: t('translate.about_dsc_time3'),
      logo: cpasLogo,
      logoAlt: 'CPAS OCMW Anderlecht',
      logoClassName: 'journey-card-logo--cpas'
    },
    {
      year: '2025',
      title: t('translate.about_title_time4'),
      description: t('translate.about_dsc_time4'),
      logo: cocofLogo,
      logoAlt: 'COCOF - Francophones Bruxelles',
      logoClassName: 'journey-card-logo--cocof'
    },
    {
      year: '2026',
      title: t('translate.about_title_time5'),
      description: t('translate.about_dsc_time5'),
      logo: partenamutLogo,
      logoAlt: 'Partenamut',
      logoClassName: 'journey-card-logo--partenamut'
    }
  ];

  const stats = [
    { icon: faUsers, value: 1000, suffix: '+', label: t('translate.stats_title') },
    { icon: faClock, number: '7 j / 7', label: t('translate.stats_title2') },
    { icon: faTrophy, value: 3, suffix: '+', label: t('translate.stats_title3') },
    { icon: faHandshake, number: 'COCOF', label: t('translate.stats_title4') }
  ];

  return (
    <div className="about-page">
      <Banner
        title={t('translate.about_principale')}
        accent={isDutch ? 'iedereen' : 'Tendue'}
        subtitle={t('translate.about_secondaire')}
      />

      <motion.section 
        className="mission-section"
        {...reveal}
        transition={{ duration: 0.55 }}
      >
        <div className="mission-content">
          <div className="mission-heading">
            <span><FontAwesomeIcon icon={faHandHoldingHeart} />{isDutch ? 'Onze missie' : 'Notre mission'}</span>
            <h2>{t('translate.about_title')}</h2>
          </div>
          <div className="mission-text-card">
            <FontAwesomeIcon icon={faShieldAlt} />
            <p>{t('translate.about_dsc')}</p>
          </div>
        </div>
      </motion.section>

      <section className="timeline-section">
        <motion.div className="inner-section-heading" {...reveal} transition={{ duration: 0.5 }}>
          <span><i></i>{isDutch ? 'Ons parcours' : 'Notre parcours'}</span>
          <h2>{isDutch ? 'Van de eerste rit tot de officiële erkenning' : 'De la première course à l’agrément officiel'}</h2>
        </motion.div>
        <div className="journey-timeline">
          <motion.span
            className="journey-line"
            aria-hidden="true"
            initial={reducedMotion ? false : { scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true, amount: 0.08 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          />
          {timelineItems.map((item, index) => (
            <div key={item.year + item.title} className={`journey-item ${index % 2 === 0 ? 'journey-left' : 'journey-right'}`}>
              <motion.span
                className="journey-dot"
                aria-hidden="true"
                initial={reducedMotion ? false : { opacity: 0, scale: 0.45 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.55 }}
                transition={{ duration: 0.35, delay: index * 0.1 }}
              />
              <motion.article
                className="journey-card"
                initial={reducedMotion ? false : { opacity: 0, x: index % 2 === 0 ? -48 : 48, y: 12 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="year">{item.year}</span>
                <div className="journey-card-title">
                  <h3>{item.title}</h3>
                  {item.logo && (
                    <span className={`journey-card-logo ${item.logoClassName}`}>
                      <img src={item.logo} alt={item.logoAlt} />
                    </span>
                  )}
                </div>
                <p>{item.description}</p>
              </motion.article>
            </div>
          ))}
        </div>
      </section>

      <section className="stats-section">
        <div className="stats-layout">
          <motion.div className="stats-intro" {...reveal} transition={{ duration: 0.5 }}>
            <span><FontAwesomeIcon icon={faUsers} />{isDutch ? 'In enkele cijfers' : 'En quelques chiffres'}</span>
            <h2>{isDutch ? 'Elke dag aanwezig voor de Brusselaars.' : 'Une présence qui compte, chaque jour, pour les Bruxellois.'}</h2>
            <p>{isDutch ? 'Een lokale vereniging, erkend en betrokken bij elk vervoer.' : 'Une association locale, agréée et engagée dans chaque accompagnement.'}</p>
          </motion.div>
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={`${stat.number || stat.value}${stat.label}`}
                className="stat-item"
                initial={reducedMotion ? false : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
              >
                <FontAwesomeIcon icon={stat.icon} className="stat-icon" />
                {typeof stat.value === 'number'
                  ? <AnimatedStatNumber value={stat.value} suffix={stat.suffix} reducedMotion={reducedMotion} label={stat.label} />
                  : <h3>{stat.number}</h3>}
                <p>{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="values-section">
        <motion.div
          className="values-content"
          initial={reducedMotion ? false : { opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inner-section-heading">
            <span><FontAwesomeIcon icon={faHeart} />{isDutch ? 'Wat ons leidt' : 'Ce qui nous guide'}</span>
            <h2>{t("translate.about_title2")}</h2>
          </div>
          <div className="values-grid">
            <div className="value-item">
              <FontAwesomeIcon icon={faHandHoldingHeart} />
              <h3>{t("translate.about_title3")} </h3>
              <p>{t("translate.about_dsc3")} </p>
            </div>
            <div className="value-item">
              <FontAwesomeIcon icon={faShieldAlt} />
              <h3>{t("translate.about_title4")} </h3>
              <p>{t("translate.about_dsc4")} </p>
            </div>
            <div className="value-item">
              <FontAwesomeIcon icon={faHandshake} />
              <h3>{t("translate.about_title5")} </h3>
              <p>{t("translate.about_dsc5")} </p>
            </div>
           
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default About;
